import { Button, Modal, Stack } from "@shopify/polaris";
import { useCallback } from "react";
import { OPEN_MODAL_ADD_PRODUCTS_USING_FILTERS } from "../../../constants/modalTypes.js";
import { closeModal } from "../../../store/features/modal.js";
import { Field, Form } from "react-final-form";
import CheckboxGroupFormField from "./components/CheckboxGroupFormField.js";
import styles from "./AddProductsUsingFiltersModal.module.css";
import AutocompleteFormField from "./components/AutocompleteFormField.jsx";
import NumberField from "./components/NumberField.jsx";
import { useAppQuery } from "../../../hooks/index.js";
import { useAppMutation } from "../../../hooks/useAppMutation.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux.js";

interface IFeatures {
  productsFeatures: {
    tags: String[],
    vendors: String[],
    types: String[]
  };
}

const AddProductsUsingFiltersModal = ({ input, onSubmit, setTotalCount, setTotalFetched, setPage }) => {
  const dispatch = useAppDispatch();
  const { isOpen, modalType } = useAppSelector((state) => state.modal);

  const { isLoading: isLoadingFeatures, data: dataFeatures } = useAppQuery<IFeatures, Error>({
    url: "/api/products/features",
    reactQueryOptions: {
      staleTime: Infinity
    },
    fetchInit: {}
  });

  const { mutate, isLoading } = useAppMutation({
    url: "/api/products/filters",
    reactMutationOptions: {
      onSuccess(data) {
        input.onChange(data.products);
        setTotalCount(data.totalCount);
        setTotalFetched(data.products.length)
        setPage(1)
        handleClose();
      }
    }
  });

  const handleClose = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const handleFormSubmit = useCallback((formValues) => {
    onSubmit(formValues);
    mutate({ ...formValues, skip: 0, take: 50 });
  }, [onSubmit, mutate]);

  const filterByStatus = [
    { label: "Active", value: "ACTIVE" },
    { label: "Draft", value: "DRAFT" },
    { label: "Archived", value: "ARCHIVED" }
  ];

  return (
    <Modal
      open={isOpen && modalType === OPEN_MODAL_ADD_PRODUCTS_USING_FILTERS}
      onClose={handleClose}
      title="Select filters"
    >
      <Form
        initialValues={{ status: "ALL", vendors: [], types: [], tags: [], priceRuleMin: null, priceRuleMax: null }}
        onSubmit={handleFormSubmit}
        keepDirtyOnReinitialize
        render={({ handleSubmit }) => (
          <>
            <Modal.Section>
              <form onSubmit={handleSubmit}>
                <Field
                  name="status"
                  component={CheckboxGroupFormField}
                  options={filterByStatus}
                  label="Filter by Status"
                />
                <Field
                  name="vendors"
                  component={AutocompleteFormField}
                  deselectedOptions={dataFeatures?.productsFeatures?.vendors || []}
                  label={"Filter products by Vendor"}
                  disabled={false}
                />
                <Field
                  name="types"
                  component={AutocompleteFormField}
                  deselectedOptions={dataFeatures?.productsFeatures?.types || []}
                  label={"Filter products by Product type"}
                  disabled={false}
                />
                <Field
                  name="tags"
                  component={AutocompleteFormField}
                  deselectedOptions={dataFeatures?.productsFeatures?.tags || []}
                  type={"text"}
                  label={"Filter products by Tags"}
                  disabled={false}
                />
                <p className={"Polaris-Labelled__LabelWrapper"}>
                  Filter products by Price Rule
                </p>
                <div className={styles.priceRulesWrapper}>
                  <Field
                    name="priceRuleMin"
                    component={NumberField}
                    type={"number"}
                    prefix={"$"}
                    text={"Min"}
                    disabled={false}
                  />
                  <Field
                    name="priceRuleMax"
                    component={NumberField}
                    type={"number"}
                    prefix={"$"}
                    text={"Max"}
                    disabled={false}
                  />
                </div>
              </form>
            </Modal.Section>
            <Modal.Section>
              <Stack distribution={"trailing"}>
                <Button
                  onClick={handleSubmit}
                  loading={isLoading}
                  primary
                >
                  Filter products and continue
                </Button>
              </Stack>
            </Modal.Section>
          </>
        )}
      />
    </Modal>
  );
};

export default AddProductsUsingFiltersModal;
